import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import { toast } from 'sonner';
import { formatPrice } from '@/lib/utils';
import { ShoppingBag, Truck, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface NewOrder {
  id: string;
  order_number: string;
  customer_name: string;
  total_price: number;
  delivery_method: string | null;
  order_type: string | null;
  status: string;
}

/**
 * AdminOrderListener - Global real-time order listener for admin users
 * Mounts in AppLayout when user is admin
 * Plays notification sound and shows sticky toast for new orders
 */
export function AdminOrderListener() {
  const navigate = useNavigate();
  const { playNotificationSound, isEnabled } = useNotificationSound();
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Skip if already initialized (avoid double subscription in dev)
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const channel = supabase
      .channel('admin-global-orders')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'orders'
        },
        (payload) => {
          const newOrder = payload.new as NewOrder;
          console.log('🔔 New order received globally:', newOrder);

          // Play notification sound
          if (isEnabled) {
            playNotificationSound();
          }

          // Determine delivery icon and text
          const isDelivery = newOrder.delivery_method === 'delivery';
          const DeliveryIcon = isDelivery ? Truck : Package;
          const deliveryText = isDelivery ? 'משלוח' : 'איסוף';

          // Show sticky toast with order details
          toast.custom(
            (t) => (
              <div 
                className="bg-white dark:bg-gray-900 border border-primary/20 shadow-xl rounded-xl p-4 w-full max-w-md animate-in slide-in-from-top-5 duration-300"
                dir="rtl"
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                    <ShoppingBag className="w-6 h-6 text-primary" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-lg text-foreground">הזמנה חדשה!</span>
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                        <DeliveryIcon className="w-3 h-3" />
                        {deliveryText}
                      </span>
                    </div>
                    
                    <div className="text-2xl font-bold text-primary mb-1">
                      {formatPrice(newOrder.total_price)}
                    </div>
                    
                    <p className="text-sm text-muted-foreground truncate">
                      {newOrder.customer_name} • #{newOrder.order_number}
                    </p>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-3">
                      <Button
                        size="sm"
                        onClick={() => {
                          toast.dismiss(t);
                          // Navigate to appropriate orders tab
                          if (isDelivery) {
                            navigate('/admin?tab=deliveries');
                          } else {
                            navigate('/admin?tab=orders');
                          }
                        }}
                        className="flex-1"
                      >
                        לצפייה
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toast.dismiss(t)}
                      >
                        סגור
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ),
            {
              duration: 15000, // 15 seconds
              position: 'top-center',
            }
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      hasInitialized.current = false;
    };
  }, [navigate, playNotificationSound, isEnabled]);

  // This component doesn't render anything visible
  return null;
}
